/**
 * useMonthlyCutsLoader Hook
 * 
 * Loads and manages monthly cuts data from the attendance service
 * Provides filtered options for year and month dropdowns
 */

import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from '@/i18n';
import { useUI } from '@/hooks/ui/useUI';
import { attendanceService } from '@/api/domains/attendance';
import {
  filterMonthlyCutsByRange,
  generateYearOptions,
  generateMonthOptions,
} from '@/api/domains/attendance/mappers/monthlyCuts.mapper';
import type { MonthlyCut } from '@/api/domains/attendance';


export const useMonthlyCutsLoader = () => {
  const { t: translate } = useTranslation();
  const { showNotification, showApiError } = useUI();

  // Monthly cuts state
  const [allMonthlyCuts, setAllMonthlyCuts] = useState<MonthlyCut[]>([]);
  const [loadingMonthlyCuts, setLoadingMonthlyCuts] = useState(false);
  const [hasAttemptedLoadMonthlyCuts, setHasAttemptedLoadMonthlyCuts] = useState(false);
  const isLoadingMonthlyCutsRef = useRef(false);

  // Load monthly cuts on mount
  const loadMonthlyCuts = useCallback(async () => {
    if (isLoadingMonthlyCutsRef.current) return;
    isLoadingMonthlyCutsRef.current = true;
    setLoadingMonthlyCuts(true);

    try {
      const response = await attendanceService.getMonthlyCutsNew();
      if (response.success && response.data) {
        setAllMonthlyCuts(response.data.cuts);
      } else {
        if (response.error?.code === 'NOT_FOUND_MONTHLY_CUTS') {
          showApiError(response, translate('attendance:overtimeReport.errors.noMonthlyCuts'));
        } else {
          showApiError(response, translate('attendance:overtimeReport.errors.loadMonthlyCuts'));
        }
      }
    } catch {
      showNotification('error', translate('attendance:overtimeReport.errors.loadMonthlyCuts'), 5000);
    } finally {
      setLoadingMonthlyCuts(false);
      setHasAttemptedLoadMonthlyCuts(true);
      isLoadingMonthlyCutsRef.current = false;
    }
  }, [showNotification, showApiError, translate]);

  // Load monthly cuts on mount
  useEffect(() => {
    if (!loadingMonthlyCuts && !hasAttemptedLoadMonthlyCuts) {
      void loadMonthlyCuts();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter cuts to last 60 months using pure function from mapper
  const monthlyCutsWithinRange = useMemo(
    () => filterMonthlyCutsByRange(allMonthlyCuts),
    [allMonthlyCuts],
  );

  // Generate year options using pure function from mapper
  const yearOptions = useMemo(
    () => generateYearOptions(monthlyCutsWithinRange),
    [monthlyCutsWithinRange],
  );

  // Generate month options function using pure function from mapper
  const monthOptions = useMemo(
    () => (selectedYear: number) => generateMonthOptions(monthlyCutsWithinRange, selectedYear),
    [monthlyCutsWithinRange],
  );

  return {
    loadingMonthlyCuts,
    monthlyCutsWithinRange,
    yearOptions,
    monthOptions,
  };
};
